import { useState, useEffect } from "react";
import { apiFetch } from "@/integrations/backend/client";

export interface SubscriptionStatus {
  has_active_subscription: boolean;
  subscription: {
    id: string;
    status: string;
    is_active: boolean;
    billing_cycle: string;
    pricing_tier: {
      id: string;
      name: string;
    } | null;
    stripe_subscription_id: string | null;
    current_period_end: string | null;
  } | null;
  transaction: {
    id: string;
    amount: number;
    currency: string;
    status: string;
    paid_at: string | null;
  } | null;
}

export function useSubscriptionStatus() {
  const [status, setStatus] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch("/api/v1/subscriptions/status", {
        method: "GET",
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({ detail: "Unknown error" }));
        throw new Error(errorData.detail || "Failed to fetch subscription status");
      }

      const data = await res.json() as SubscriptionStatus;
      setStatus(data);
    } catch (err: any) {
      console.error("Failed to fetch subscription status:", err);
      setError(err.message || "Failed to fetch subscription status");
      // Set default status on error
      setStatus({
        has_active_subscription: false,
        subscription: null,
        transaction: null,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  return {
    hasActiveSubscription: status?.has_active_subscription ?? false,
    subscription: status?.subscription ?? null,
    transaction: status?.transaction ?? null,
    loading,
    error,
    refetch: fetchStatus,
  };
}

